import { Layer } from '@trimble/web3d-providers';
import { Component, Show } from 'solid-js';
import { t } from '@trimble/i18n';
import {
  AutoComplete,
  AutoCompleteSelectEvent,
  AutoCompleteItem,
} from '@trimble/web3d-web-components';
import { ColorProperty } from '../color-property/ColorProperty';
import { LineWeightIcon } from '../line-weight-icon/LineWeightIcon';

export interface LayerGeneralPropertiesPanelProps {
  nameToDisplay: string;
  colorToDisplay: string;
  entityColor?: string | null;
  lineThicknessToDisplay: string;
  lineStyleToDisplay: string;
  canEdit: boolean;
  allLayers: Layer[];
  byLayerColor?: string | null;
  isColorChangeInProgress?: boolean;
  onLayerSelected?(layerId: number): void;
  onByLayerSelect?(): void;
  onColorChange?(color: string | null): void;
  /** When false, hides line style and line weight (e.g. for text CAD objects). */
  showLineProperties?: boolean;
}

export const LayerGeneralPropertiesPanel: Component<
  LayerGeneralPropertiesPanelProps
> = (props) => {
  const layerDropdown = (): AutoCompleteItem[] => {
    return props.allLayers.map((x) => {
      return {
        label: x.name,
        value: String(x.id),
      };
    });
  };

  const layerChange = (e: AutoCompleteSelectEvent): void => {
    if (e.item?.value && props.onLayerSelected) {
      props.onLayerSelected(parseFloat(e.item.value));
    }
  };

  return (
    <div class="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 items-center">
      <span class="text-right w-[92px]">{t('general.layer')}</span>

      <div
        class={`pl-1 py-1 rounded flex gap-x-4 z-[500000] ${props.canEdit ? 'border-gray-6 border' : 'bg-gray-light'}`}
      >
        <modus-wc-icon size="sm" class="h-[14px] my-auto" name="layer" />
        <Show
          when={props.canEdit}
          fallback={<span data-testid="layer-name">{props.nameToDisplay}</span>}
        >
          <AutoComplete
            name="layer-name"
            text={props.nameToDisplay}
            placeholder={t('general.searchLayers')}
            menuItems={layerDropdown()}
            onItemSelect={layerChange}
            height={150}
            noBorder={true}
          />
        </Show>
      </div>

      <span class="text-right w-[92px]">{t('general.color')}</span>
      <div
        class={`pl-1 rounded flex gap-x-4 z-[50000] ${props.canEdit ? 'border-gray-6 border' : 'bg-gray-light'}`}
      >
        <ColorProperty
          selectedColor={props.colorToDisplay}
          objectColor={props.entityColor}
          byLayerColor={props.byLayerColor}
          isByLayer={true}
          canEdit={props.canEdit}
          disabled={props.isColorChangeInProgress}
          onByLayerSelect={props.onByLayerSelect}
          cssAlignment="right-[1px]"
          onColorChange={props.onColorChange}
          noBorder={true}
          hideIcon={false}
        />
      </div>

      <Show when={props.showLineProperties ?? true}>
        <span class="text-right ">{t('general.linestyle')}</span>
        <div class="bg-gray-light p-1 rounded flex text-gray-6 gap-x-4">
          <modus-wc-icon
            size="sm"
            class="h-[20px] my-auto"
            name="polygon_line_tool"
          />
          <div data-testid="layer-line-style">{props.lineStyleToDisplay}</div>
        </div>

        <span class="text-right ">{t('general.lineweight')}</span>
        <div class="bg-gray-light p-1 rounded flex text-gray-6 gap-x-4">
          <LineWeightIcon size="20" />
          <div data-testid="layer-line-thickness">
            {`${props.lineThicknessToDisplay} units`}
          </div>
        </div>
      </Show>
    </div>
  );
};
